<template>
  <div>
    <v-alert
      v-if="!clientId"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      Selecciona un cliente para cargar las ventas pendientes.
    </v-alert>

    <v-data-table
      v-else
      :headers="headers"
      :items="pendingSales"
      :items-per-page="10"
      class="elevation-0"
      hide-default-footer
    >
      <template #item.folio="{ item }">
        #{{ item?.id }}
      </template>
      <template #item.amount="{ item }">
        {{ currency(item.attributes?.amount) }}
      </template>
      <template #item.applied="{ item, index }">
        <v-text-field
          v-model="applied[index]"
          type="number"
          density="compact"
          variant="solo-filled"
          hide-details
          style="max-width: 140px;"
        />
      </template>
      <template #item.paid="{ item }">
        {{ currency(item.attributes?.paid || 0) }}
      </template>
      <template #item.balance="{ item, index }">
        {{ currency(getBalance(item, applied[index])) }}
      </template>
    </v-data-table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref, nextTick } from 'vue';
import { useSalesStore } from '@/store/sales';

const props = defineProps<{
  modelValue: any[];
  clientId?: number | null;
}>();

const emit = defineEmits(['update:modelValue']);

const salesStore = useSalesStore();
const pendingSales = ref<any[]>([]);
const applied = ref<(number | string)[]>([]);
let syncing = false;

const headers = [
  { title: 'Folio Venta', value: 'folio' },
  { title: 'Monto venta', value: 'amount' },
  { title: 'Monto aplicado', value: 'applied' },
  { title: 'Monto abonado', value: 'paid' },
  { title: 'Saldo pendiente', value: 'balance' },
];

const currency = (val: number | undefined | null) => {
  const num = Number(val) || 0;
  return `$${num.toFixed(2)}`;
};

const getBalance = (sale: any, appliedVal: any) => {
  const basePaid = Number(sale?.attributes?.paid || 0);
  const saleAmount = Number(sale?.attributes?.amount || 0);
  const appliedNum = Number(appliedVal || 0);
  const newPaid = basePaid + appliedNum;
  return saleAmount - newPaid;
};

const syncFromModel = async () => {
  syncing = true;
  applied.value = pendingSales.value.map((sale) => {
    const match = props.modelValue?.find((m: any) => m.sale_id === sale.id);
    return match?.amount || 0;
  });
  await nextTick();
  syncing = false;
};

const emitChanges = () => {
  if (syncing) return;
  const payload = pendingSales.value.map((sale, idx) => ({
    sale_id: sale.id,
    amount: Number(applied.value[idx] || 0),
  }));
  emit('update:modelValue', payload);
};

const loadPendingSales = async () => {
  if (!props.clientId) {
    pendingSales.value = [];
    applied.value = [];
    emit('update:modelValue', []);
    return;
  }

  const params = {
    'filter[client_id]': props.clientId,
    'filter[status]': 'Pendiente',
  };
  const res = await salesStore.searchSales(params);
  pendingSales.value = res.data || res || [];
  syncFromModel();
};

watch(() => props.clientId, loadPendingSales, { immediate: true });

watch(applied, emitChanges, { deep: true });
watch(() => props.modelValue, syncFromModel, { deep: true });

onMounted(loadPendingSales);
</script>
